import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import {
  excluirArquivo,
  previaExclusaoArquivo,
  visaoArquivos,
} from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Botao,
  Busca,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  Painel,
  StatusArquivoBadge,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { useAuth } from '../contexts/AuthContext';
import { useOrdenacao } from '../hooks/useOrdenacao';
import { usePaginacao } from '../hooks/usePaginacao';
import { brl, formatDateTime, numero } from '../utils/formato';

/* ── Confirmar a exclusão de um recebimento ────────────────────────────
 *
 * Apagar um arquivo não é operação de rotina: desfaz uma conciliação inteira
 * e muda o valor a baixar daquele protocolo. Por isso a tela pergunta com
 * números, e não com um "tem certeza?" genérico — e diz também **o que fica**,
 * porque é a parte preservada que torna a decisão reversível na prática.
 */
function ConfirmarExclusaoArquivo({ arquivo, aoFechar, aoExcluir }) {
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState('');
  const [agindo, setAgindo] = useState(false);
  const [limparResolucoes, setLimparResolucoes] = useState(false);

  useEffect(() => {
    previaExclusaoArquivo(arquivo.id_arquivo)
      .then((r) => setPrevia(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível ler o arquivo.')));
  }, [arquivo.id_arquivo]);

  async function confirmar() {
    setAgindo(true);
    setErro('');
    try {
      await excluirArquivo(arquivo.id_arquivo, { limparResolucoes });
      aoExcluir(`"${arquivo.nome_original}" foi excluído.`);
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível excluir.'));
      setAgindo(false);
    }
  }

  const sai = previa?.sai || {};
  const itens = [
    ['guias', 'guias'],
    ['linhas', 'linhas de procedimento'],
    ['glosas', 'itens de glosa'],
    ['divergencias', 'divergências'],
    ['pendencias', 'pendências'],
  ].filter(([k]) => sai[k] > 0);

  return (
    <>
      <div className="modal-backdrop fade show" style={{ background: 'rgba(16,24,40,.5)' }} />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div
            className="modal-content"
            style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
          >
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <div>
                <h2 className="ff-painel__titulo">Excluir recebimento</h2>
                <p className="ff-painel__sub">
                  {arquivo.nome_original}
                  {arquivo.competencia ? ` · competência ${arquivo.competencia}` : ''}
                </p>
              </div>
              <button type="button" className="btn-close" onClick={aoFechar} aria-label="Fechar" />
            </div>

            <div className="modal-body d-flex flex-column gap-3">
              <Alerta tipo="erro">{erro}</Alerta>

              {!previa ? (
                <LoadingSpinner />
              ) : (
                <>
                  <div className="ff-aviso ff-aviso--atencao" style={{ margin: 0 }}>
                    <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                    <div>
                      <strong>Isto desfaz a conciliação deste arquivo.</strong> O
                      valor a baixar dos protocolos que ele alimentava muda junto.
                      Não há como desfazer.
                    </div>
                  </div>

                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                      Vai ser apagado
                    </div>
                    {itens.length === 0 ? (
                      <p style={{ fontSize: 13, color: 'var(--ff-muted)', margin: 0 }}>
                        Nada foi extraído deste arquivo — só o registro do envio.
                      </p>
                    ) : (
                      <ul style={{ fontSize: 14, marginBottom: 0 }}>
                        {itens.map(([k, rotulo]) => (
                          <li key={k}>
                            <strong>{numero(sai[k])}</strong> {rotulo}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* O que fica é o que torna a decisão reversível na prática:
                      reenviar o arquivo não vai consultar de novo a produção
                      do cliente pelas guias já resolvidas. */}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                      Vai continuar existindo
                    </div>
                    <ul style={{ fontSize: 14, color: 'var(--ff-muted)', marginBottom: 0 }}>
                      {!limparResolucoes && (
                        <li>
                          <strong>
                            {numero(previa.fica.resolucoes_de_protocolo)}
                          </strong>{' '}
                          vínculo(s) guia → protocolo já resolvidos. Eles não mudam
                          depois de faturado, então reenviar o arquivo{' '}
                          <strong>não</strong> consulta o Bioslab de novo por eles.
                        </li>
                      )}
                      <li>
                        A trilha de auditoria, com os números de agora — é por ela
                        que esta exclusão continua explicável depois.
                      </li>
                    </ul>
                  </div>

                  {previa.fica.resolucoes_de_protocolo > 0 && (
                    <label
                      className="d-flex align-items-start gap-2 p-3"
                      style={{
                        border: '1px solid var(--ff-border-soft)',
                        borderRadius: 10,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={limparResolucoes}
                        onChange={(e) => setLimparResolucoes(e.target.checked)}
                        style={{ marginTop: 3 }}
                      />
                      <span style={{ fontSize: 13 }}>
                        <strong>
                          Apagar também os {numero(previa.fica.resolucoes_de_protocolo)}{' '}
                          vínculos guia → protocolo
                        </strong>
                        <span style={{ display: 'block', color: 'var(--ff-muted)' }}>
                          Só marque para <strong>testar o fluxo do zero</strong>: sem
                          isso, reenviar este arquivo reaproveita o que já foi
                          resolvido e não chega a consultar o Bioslab — o que é
                          certo no dia a dia e um falso positivo num teste de
                          conciliação. Marcando, a próxima ingestão consulta a
                          produção do cliente de novo.
                        </span>
                      </span>
                    </label>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <Botao variante="secundario" onClick={aoFechar} disabled={agindo}>
                Cancelar
              </Botao>
              <button
                type="button"
                className="ff-btn ff-btn--perigo"
                onClick={confirmar}
                disabled={agindo || !previa}
              >
                {agindo ? 'Excluindo…' : 'Excluir definitivamente'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function Arquivos() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [aExcluir, setAExcluir] = useState(null);
  const [aviso, setAviso] = useState('');
  const [itens, setItens] = useState([]);
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(
    () =>
      visaoArquivos()
        .then((r) => setItens(r.data))
        .catch((e) =>
          setErro(mensagemErro(e, 'Não foi possível carregar os arquivos.'))
        )
        .finally(() => setCarregando(false)),
    []
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return itens;
    return itens.filter(
      (a) =>
        a.operadora?.toLowerCase().includes(t) ||
        a.nome_original?.toLowerCase().includes(t) ||
        a.competencia?.toLowerCase().includes(t)
    );
  }, [itens, busca]);

  const ord = useOrdenacao(filtrados, 'created_at', false);
  const pag = usePaginacao(ord.ordenados, 15);

  if (carregando) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        titulo="Arquivos recebidos"
        subtitulo="Cada demonstrativo, com o resultado das conferências contra o próprio documento."
        acoes={
          <Link to="/arquivos/novo" className="ff-btn ff-btn--primario">
            <i className="bi bi-upload" aria-hidden="true" />
            Lançar recebimento
          </Link>
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="ok" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      <Painel
        titulo="Demonstrativos"
        subtitulo={`${itens.length} arquivo${itens.length === 1 ? '' : 's'} recebido${itens.length === 1 ? '' : 's'}`}
        busca={
          itens.length > 0 && (
            <Busca
              valor={busca}
              onChange={setBusca}
              placeholder="Buscar arquivo ou operadora"
              id="busca-arquivos"
            />
          )
        }
        rodape={filtrados.length > 0 && <Paginacao {...pag} rotulo="arquivos" />}
      >
        {itens.length === 0 ? (
          <Vazio
            icone="bi-file-earmark-text"
            titulo="Nenhum arquivo recebido"
            descricao="Envie o demonstrativo de pagamento de uma operadora para começar a conciliação."
            acao={
              <Link to="/arquivos/novo" className="ff-btn ff-btn--primario">
                Lançar recebimento
              </Link>
            }
          />
        ) : filtrados.length === 0 ? (
          <Vazio
            icone="bi-search"
            titulo="Nenhum arquivo corresponde à busca"
            descricao={`Nada encontrado para "${busca}".`}
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th campo="operadora" estado={ord}>
                  Operadora
                </Th>
                <Th campo="nome_original" estado={ord}>
                  Arquivo
                </Th>
                <Th campo="competencia" estado={ord}>
                  Competência
                  <Ajuda titulo="Competência" origem="Informada no upload · cabeçalho do demonstrativo">
                    Mês em que a operadora efetuou o pagamento. Não é o mês do atendimento —
                    um único pagamento costuma cobrir atendimentos de vários meses.
                  </Ajuda>
                </Th>
                <Th campo="qtd_guias" estado={ord} alinhar="right">
                  Guias
                </Th>
                <Th campo="total_extraido" estado={ord} alinhar="right">
                  Bruto pago
                  <Ajuda
                    titulo="Bruto pago"
                    origem="Arquivo da operadora · soma de Valor (R$)"
                  >
                    Tudo que a operadora pagou neste arquivo, antes de impostos retidos e
                    descontos. É deste valor que saem as baixas por protocolo.
                  </Ajuda>
                </Th>
                <Th alinhar="right">
                  Retido/desc.
                  <Ajuda
                    titulo="Retido e descontado"
                    origem="bruto pago − líquido creditado"
                  >
                    Impostos retidos na fonte (PIS/COFINS/CSLL, IRRF, ISS) mais descontos
                    gerais. NÃO é erro de conciliação: existe só no total do arquivo, sem
                    vínculo com guia, e por isso não entra na baixa.
                  </Ajuda>
                </Th>
                <Th>
                  Conferências
                  <Ajuda titulo="Conferências aritméticas">
                    Comparam o que foi extraído com os totais que a própria operadora
                    imprimiu no documento. Três em verde significa que a leitura está
                    provada, não apenas plausível.
                  </Ajuda>
                </Th>
                <Th campo="status" estado={ord}>
                  Status
                </Th>
                <Th campo="created_at" estado={ord} alinhar="right">
                  Recebido em
                </Th>
                {isAdmin && <Th alinhar="right">Administração</Th>}
              </tr>
            </thead>
            <tbody>
              {pag.paginados.map((a) => {
                const conf = a.conferencias || [];
                const falhas = conf.filter((c) => !c.passou).length;
                return (
                  <tr
                    key={a.id_arquivo}
                    className="ff-linha-clicavel"
                    onClick={() => navigate(`/arquivos/${a.id_arquivo}`)}
                  >
                    <td className="col-principal">{a.operadora}</td>
                    <td className="col-codigo">{a.nome_original}</td>
                    <td className="col-meta">{a.competencia || '—'}</td>
                    <td className="col-qtd">{numero(a.qtd_guias)}</td>
                    <td className="col-valor">{brl(a.total_extraido)}</td>
                    <td className="col-qtd">
                      {/* Impostos retidos + auditoria: não é erro de conciliação. */}
                      {a.diferenca_bruto_liquido ? brl(a.diferenca_bruto_liquido) : '—'}
                    </td>
                    <td>
                      {conf.length === 0 ? (
                        <span className="col-meta">—</span>
                      ) : falhas === 0 ? (
                        <span className="ff-badge ff-badge--confirmado">
                          {conf.length} de {conf.length}
                        </span>
                      ) : (
                        <span className="ff-badge ff-badge--reprovado">
                          {falhas} falha{falhas === 1 ? '' : 's'}
                        </span>
                      )}
                    </td>
                    <td>
                      <StatusArquivoBadge status={a.status} />
                    </td>
                    <td className="col-data">{formatDateTime(a.created_at)}</td>
                    {isAdmin && (
                      <td className="col-data" style={{ textAlign: 'right' }}>
                        {/* `stopPropagation` porque a linha inteira navega
                            para o detalhe — sem isso, clicar em Excluir
                            abriria o arquivo por baixo do diálogo. */}
                        <Botao
                          variante="secundario"
                          pequeno
                          onClick={(e) => {
                            e.stopPropagation();
                            setAExcluir(a);
                          }}
                          title="Apaga o recebimento e tudo que foi apurado a partir dele. Os vínculos guia → protocolo já resolvidos são preservados."
                        >
                          Excluir
                        </Botao>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        )}
      </Painel>

      {aExcluir && (
        <ConfirmarExclusaoArquivo
          arquivo={aExcluir}
          aoFechar={() => setAExcluir(null)}
          aoExcluir={(msg) => {
            setAExcluir(null);
            setAviso(msg);
            carregar();
          }}
        />
      )}
    </>
  );
}
